import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { ButtonThemeService, THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { ChildrenPropType } from '../../prop-types/children';
import { useDispatch, useSelector } from 'react-redux';

import { useGetBatchCollectionsFromState } from '../../containers/batches/get-batch-collections-from-state';
import { SalesOrderStatusService } from '../../services/sales-order-status-service';
import { createBatchWithSalesOrders } from '../../store/batches/batches.actions';
import { loadMoreActiveBatches } from '../../store/batches/nav/batches.nav.actions';
import Global from '../../store/global-selectors';
import { addSalesOrderToBatchFromOrderDetails } from '../../store/orders/order-details/orders.details.actions';
import {
  ORDER_DETAILS_ACTIONS,
  useTrackOrderDetailsAction,
} from '../../orders/modals/order-details-modal/event-tracking/use-track-order-details-action';
import { emptyArray } from 'ui-components/default-props/empty';

import AddToBatchDropdown from './index';

const OrderDetailsAddToBatchDropdownContainer = ({
  children,
  hideChevron = false,
  menuPosition,
  salesOrderId,
  tabIndex = 0,
  togglerClassName = '',
  togglerTheme = THEME_CONSTANTS.white,
  togglerOpenClassname = '',
}) => {
  const fulfillmentPlanIds =
    useSelector((state) =>
      Global.selectEntitiesSalesOrderProperty(state, {
        salesOrderId,
        property: 'fulfillmentPlanIds',
      }),
    ) || emptyArray;
  const fulfillmentPlans = useSelector((state) => Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds })) || emptyArray;
  const userId = useSelector(Global.selectUserCurrentUserId);
  const pageSize = useSelector(Global.selectBatchesNavPageSize);
  const totalBatchesCount = useSelector(Global.selectBatchesNavTotalBatchesCount);
  const shouldShowLoadMoreButton = pageSize < totalBatchesCount;

  const dispatch = useDispatch();
  const trackAddToBatchClick = useTrackOrderDetailsAction(ORDER_DETAILS_ACTIONS.ADD_SALES_ORDER_TO_BATCH);

  const addIdsToBatch = useCallback(
    (fulfillmentPlanSetId) => {
      dispatch(addSalesOrderToBatchFromOrderDetails(fulfillmentPlanSetId, salesOrderId));
      trackAddToBatchClick();
    },
    [dispatch, trackAddToBatchClick, salesOrderId],
  );

  const createBatchWithIds = useCallback(() => {
    const newBatchName = undefined;
    dispatch(createBatchWithSalesOrders(newBatchName, userId, [salesOrderId]));
    trackAddToBatchClick();
  }, [dispatch, salesOrderId, trackAddToBatchClick, userId]);

  const loadMore = useCallback(() => {
    dispatch(loadMoreActiveBatches());
  }, [dispatch]);

  const { myBatches, otherBatches } = useGetBatchCollectionsFromState();

  return (
    <AddToBatchDropdown
      addIdsToBatch={addIdsToBatch}
      createBatchWithIds={createBatchWithIds}
      disabled={SalesOrderStatusService.determineIfCancelled(fulfillmentPlans)}
      hideChevron={hideChevron}
      ids={[salesOrderId]}
      loadMore={loadMore}
      menuPosition={menuPosition}
      myBatches={myBatches}
      otherBatches={otherBatches}
      shouldShowLoadMoreButton={shouldShowLoadMoreButton}
      tabIndex={tabIndex}
      togglerClassName={togglerClassName}
      togglerOpenClassname={togglerOpenClassname}
      togglerTheme={togglerTheme}
      userId={userId}
    >
      {children}
    </AddToBatchDropdown>
  );
};

OrderDetailsAddToBatchDropdownContainer.propTypes = {
  children: ChildrenPropType,
  hideChevron: PropTypes.bool,
  menuPosition: PropTypes.string,
  salesOrderId: PropTypes.string.isRequired,
  tabIndex: PropTypes.number,
  togglerClassName: PropTypes.string,
  togglerTheme: PropTypes.oneOf(ButtonThemeService.getButtonThemes()),
  togglerOpenClassname: PropTypes.string,
};

export { OrderDetailsAddToBatchDropdownContainer };
